/**
 * How many plans of each kind the workspace holds — the three totals across
 * the top of the Create plan screen.
 *
 * One call for all three, where each list used to be asked for a page of one
 * row so its `count` could be read off the envelope. The figures are the
 * server's, whichever list is open under them, and a workspace that has never
 * been able to write a plan gets nought of each rather than an error: 403 is
 * what that workspace is answered with, and its lists draw as empty for the
 * same reason. See `useMealPlans`.
 *
 * The key is under `plans`, the meal plan module's prefix — but the figure
 * moves whenever any kind is written, so the supplement and exercise modules
 * throw it away beside their own caches. See `useSupplementPlanWrites` and
 * `useExercisePlans`. Meal plan writes leave the screen for the builder, and
 * a remount asks again.
 */

import { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { ApiError } from "../../../lib/http";
import { useTokenGetter } from "../../auth/hooks/useTokenGetter";
import { fetchPlanCounts } from "../services/mealPlansService";
import type { PlanCounts } from "../types/plans.types";

export type PlanCountsStatus = "loading" | "ready" | "failed";

export interface PlanCountsState {
  /** The three figures. Nought each while the call is out or has failed. */
  counts: PlanCounts;
  status: PlanCountsStatus;
}

export const PLAN_COUNTS_QUERY_KEY = ["plans", "counts"] as const;

const NO_COUNTS: PlanCounts = { meal: 0, supplement: 0, exercise: 0 };

export function usePlanCounts(): PlanCountsState {
  const getToken = useTokenGetter();

  const query = useQuery({
    queryKey: PLAN_COUNTS_QUERY_KEY,
    queryFn: ({ signal }) => fetchPlanCounts(getToken, signal),
  });

  // 403 is this endpoint's answer to a workspace with no plan running — not
  // news, and not something to log as though it were.
  const refused = query.error instanceof ApiError && query.error.status === 403;

  useEffect(() => {
    if (query.isError && !refused)
      console.error("Plan counts failed to load:", query.error);
  }, [query.isError, query.error, refused]);

  return {
    counts: query.isError ? NO_COUNTS : query.data ?? NO_COUNTS,
    status: query.isError
      ? refused
        ? "ready"
        : "failed"
      : query.isFetching
      ? "loading"
      : "ready",
  };
}
